import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { TwoFactorAuthService, TwoFactorModalContext, TwoFactorModalState } from 'src/app/services/two-factor-auth.service';

const DIGITS_COUNT = 6;

@Component({
  selector: 'two-factor-otp-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './two-factor-otp-modal.component.html',
  styleUrl: './two-factor-otp-modal.component.scss'
})
export class TwoFactorOtpModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('content') private content!: TemplateRef<unknown>;

  ctx: TwoFactorModalContext | null = null;
  digits: string[] = Array(DIGITS_COUNT).fill('');
  shake: boolean = false;

  private modalRef?: NgbModalRef;
  private wasOpen: boolean = false;
  private previousError: string | null = null;
  private readonly subscription = new Subscription();

  constructor(
    private twoFactorAuthService: TwoFactorAuthService,
    private modalService: NgbModal
  ) { }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.twoFactorAuthService.state$.subscribe((state: TwoFactorModalState) => {
        if (state.isOpen && state.context) {
          this.ctx = state.context;
          this.openModal();
        } else {
          this.closeModal();
        }

        if (state.isOpen && !this.wasOpen) {
          this.resetDigits();
          this.focusInputAt(0);
        }
        this.wasOpen = state.isOpen;

        const currentError = state.context?.error ?? null;
        if (currentError && currentError !== this.previousError) {
          this.triggerShake();
        }
        this.previousError = currentError;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.closeModal();
  }

  private openModal(): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.open(this.content, {
      centered: true,
      backdrop: 'static',
      windowClass: 'two-factor-modal'
    });

    // Chiusura con ESC: equivale a "Cancel".
    this.modalRef.result.catch(() => {
      this.modalRef = undefined;
      if (this.ctx) {
        this.cancel();
      }
    });
  }

  private closeModal(): void {
    this.ctx = null;
    const ref = this.modalRef;
    this.modalRef = undefined;
    ref?.dismiss();
  }

  private resetDigits(): void {
    this.digits = Array(DIGITS_COUNT).fill('');
  }

  /* Le cifre vivono in un ng-template renderizzato da NgbModal fuori dalla view
     del componente, quindi @ViewChildren non le intercetta: le cerchiamo nel DOM
     del modale, identificato dalla sua windowClass. */
  private focusInputAt(index: number): void {
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>(
        '.two-factor-modal .two-factor-digit-input'
      );
      inputs.item(index)?.focus();
    });
  }

  private triggerShake(): void {
    this.shake = false;
    setTimeout(() => this.shake = true);
    setTimeout(() => this.shake = false, 400);
  }

  get code(): string {
    return this.digits.join('');
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '');
    this.digits[index] = value.slice(-1) || '';
    input.value = this.digits[index];

    if (this.digits[index] && index < DIGITS_COUNT - 1) {
      this.focusInputAt(index + 1);
    }
  }

  onKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      this.digits[index - 1] = '';
      this.focusInputAt(index - 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text').replace(/\D/g, '') ?? '';
    if (!pasted) {
      return;
    }
    event.preventDefault();

    for (let i = 0; i < DIGITS_COUNT; i++) {
      this.digits[i] = pasted[i] ?? '';
    }

    const nextEmpty = this.digits.findIndex(digit => !digit);
    this.focusInputAt(nextEmpty === -1 ? DIGITS_COUNT - 1 : nextEmpty);
  }

  submit(): void {
    if (this.code.length !== DIGITS_COUNT) {
      return;
    }
    this.twoFactorAuthService.submitOtp(this.code);
  }

  cancel(): void {
    this.resetDigits();
    this.twoFactorAuthService.close();
  }
}
